import React, { useState } from "react";
import styled from "styled-components";
// import Table from "rc-table";
import DataTable, { createTheme } from "react-data-table-component";

import {
  formatName,
  numberWithCommas,
  abbreviateNumber,
} from "helpers/miscHelpers";

createTheme("myDark", {
  text: {
    primary: "#ccc",
    secondary: "#2aa198",
  },
  background: {
    default: "rgba(40, 40, 40, 1)",
  },
  context: {
    background: "#cb4b16",
    text: "#FFFFFF",
  },
  divider: {
    default: "rgba(102, 33, 134, 0.4)",
  },
  button: {
    default: "#2aa198",
    hover: "rgba(0,0,0,.08)",
    focus: "rgba(255,255,255,.12)",
    disabled: "rgba(0,0,0,.12)",
  },
  sortFocus: {
    default: "#2aa198",
  },
  highlightOnHover: {
    default: "#EEEEEE",
    text: "rgba(0, 0, 0, 0.87)",
  },
  striped: {
    default: "rgba(60, 60, 60, 0.5)",
    text: "#ccc",
  },
});

const FilterBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: 0 0 10px 0;
  padding: 1rem;

  input[type="text"] {
    height: 34px;
    width: 200px;
    border-radius: 3px;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border: 1px solid #e5e5e5;
    padding: 0 32px 0 16px;
  }

  button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    height: 34px;
    width: 32px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &:hover {
    cursor: pointer;
  }
`;

const FilterComponent = ({ filterText, onFilter, onClear }) => (
  <FilterBox>
    <input
      id="search"
      type="text"
      placeholder="Filter By Name"
      value={filterText}
      onChange={onFilter}
    />
    <button type="button" onClick={onClear}>
      X
    </button>
  </FilterBox>
);

const columns = [
  {
    name: "Country",
    selector: "name",
    sortable: true,
    format: (row) => `${formatName(row.name)}`,
  },
  {
    name: "Total Cases",
    selector: "cases",
    sortable: true,
    format: (row) => `${numberWithCommas(row.cases)}`,
  },
  {
    name: "New Cases",
    selector: "newCases",
    sortable: true,
    format: (row) => `${numberWithCommas(row.newCases)}`,
  },
  {
    name: "Total Deaths",
    selector: "deaths",
    sortable: true,
    format: (row) => `${numberWithCommas(row.deaths)}`,
  },
  {
    name: "New Deaths",
    selector: "newDeaths",
    sortable: true,
    format: (row) => `${numberWithCommas(row.newDeaths)}`,
  },
  {
    name: "Cases per 100K",
    selector: "casesPer100k",
    sortable: true,
    format: (row) =>
      `${row.casesPer100k ? abbreviateNumber(row.casesPer100k, 3) : 0}`,
    conditionalCellStyles: [
      {
        when: (row) => row.casesPer100k > 100,
        style: {
          backgroundColor: "rgba(220,50,50, 0.4)",
          color: "#111",
          "&:hover": {
            cursor: "pointer",
          },
        },
      },
      {
        when: (row) => row.casesPer100k > 500,
        style: {
          backgroundColor: "rgba(220,50,50, 0.8)",
          color: "#111",
          "&:hover": {
            cursor: "pointer",
          },
        },
      },
    ],
  },
  {
    name: "Deaths per 100K",
    selector: "deathsPer100k",
    sortable: true,
    format: (row) =>
      `${row.deathsPer100k ? abbreviateNumber(row.deathsPer100k, 3) : 0}`,
  },
];

const MyDataTable = ({ data }) => {
  const [filterText, setFilterText] = useState("");

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        // setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };

    return (
      <FilterComponent
        onFilter={(e) => setFilterText(e.target.value)}
        onClear={handleClear}
        filterText={filterText}
      />
    );
  }, [filterText]);

  const latestDate = Object.keys(data[0].timeline.cases)
    .filter((x) => x.includes("/"))
    .slice(-1)[0];

  const excludedCountries = ["Guinea Bissau"];

  const arrangedData = data
    .filter((d) => !excludedCountries.includes(d.country))
    .map((d, i) => {
      const tempObj = {
        name: d.country,
        cases: d.timeline.cases[`${latestDate}`],
        deaths: d.timeline.deaths[`${latestDate}`],
        newCases: d.timeline.newCases[`${latestDate}`],
        newDeaths: d.timeline.newDeaths[`${latestDate}`],
        casesPer100k: d.timeline.casesPer100k[`${latestDate}`],
        deathsPer100k: d.timeline.deathsPer100k[`${latestDate}`],
        key: i,
      };
      return tempObj;
    });

  const filteredItems = arrangedData.filter(
    (item) =>
      item.name && item.name.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={filteredItems}
        title={`Daily Data For: ${latestDate}`}
        striped
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        // theme="myDark"
        noHeader
      />
    </>
  );
};

export default MyDataTable;
